/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import { HttpClient, HttpErrorResponse, HttpEvent } from "@angular/common/http";
import { inject, Injectable, Injector } from "@angular/core";
import { catchError, Observable, of, tap, map } from "rxjs";
import { IResponseApi } from "@core/models";
import { ERROR_CODE } from "@core/constants";
import { AppMessages } from "@shared/constants";
// CHÚ Ý: Luôn import trực tiếp từ file nguồn, KHÔNG import AuthService từ barrel "@core/authentication"
// để tránh lỗi Circular Dependency khiến Class extends value undefined.
import { AuthService } from "../authentication/auth.service";
import { environment } from "@environments/environment";
import { castInt, parseParams } from "@shared/utils";
// import { AuthService } from "../authentication"; // Phụ thuộc vòng - sử dụng injector bên dưới

type resTypeHttp = any & 'arraybuffer' | 'blob' | 'json' | 'text';

/**
 * Handler class
 *    1. Func get data common.
 *    2. Func format data.
 *    3. Func some thing
 * */
@Injectable({
  providedIn: 'any'
})
export class ApiService {
  httpClient = inject(HttpClient);
  injector = inject(Injector);
  authService = inject(AuthService);

  /**
   * ---------------------------------------------------------------------------------------------------------
   * Format data ------------------------------------------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------
   * */
  getParamsCommon(params: any = {}, isFormData = false) {
    if (isFormData) {
      params.append('businessId', this.authService.businessCurrent?.id);
      return params;
    }

    if (!params['businessId']) {
      params['businessId'] = this.authService.businessCurrent?.id;
    }
    return params;
  }

  getBaseUrlApi(endpoint: string): string {
    return environment.baseUrlApi + endpoint;
  }



  getResponseDefault<T = IResponseApi>(messages: any[] = [AppMessages.INVALID_DATA]) {
    return of({
      code: 0,
      errorCode: null,
      statusCode: '',
      messages: messages,
      data: [],
    }) as Observable<T>;
  }

  /**
   * ---------------------------------------------------------------------------------------------------------
   * Request method api Nhanh ----------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------
   * */

  handleError(messages: any = null) {
    return catchError<any | Observable<IResponseApi>, any>((err: HttpErrorResponse) => {
      const body: IResponseApi<[]> = {
        code: 0,
        errorCode: null,
        statusCode: castInt(err.status),
        messages: [],
        data: [],
      }
      return of(body);
    }
    )
  }

  private handleTapRes(): any {
    return tap({
      next: (res: IResponseApi) => {
        if (res.errorCode == ERROR_CODE.ERR_PAGE_401) {
          // Something code handle
        }
      }
    });
  }

  /**
   * Chuẩn hóa response từ BE:
   * - V1 (jsonModel cũ): { msg: {...} }
   * - V2 (ApiResultModel mới): { code: 1, data: { msg: {...} }, messages: [] }
   *
   * Nếu response là V1 (thiếu code/data), wrap vào V2 format để FE luôn xử lý đồng bộ.
   */
  private normalizeV1Response<T = IResponseApi>(response: T): T {
    if (response && typeof response === 'object' && !('code' in (response as any)) && 'msg' in (response as any)) {
      return { code: 1, data: response, messages: [] } as unknown as T;
    }
    return response;
  }


  postFormDataRequest<T = IResponseApi>(endpoint: string, formData: FormData): Observable<T | any> {
    formData = this.getParamsCommon(formData);

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), formData).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  /**
   * Used in cases where it is necessary to monitor upload/download progress events.
   * @returns An Observable of HttpEvent<object> containing progress and response events.
   */
  postFormDataRequestProgress<T = IResponseApi>(endpoint: string, formData: FormData): Observable<HttpEvent<object>> {
    formData = this.getParamsCommon(formData);

    const options = {
      reportProgress: true,
      observe: "events" as const
    }
    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), formData, options).pipe(
      this.handleTapRes(),
      this.handleError()
    );
  }


  /**
   * Chú ý API hầu hết đều dùng POST nên cần xem tài liệu trước khi dùng
   * */
  getRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    if (!this.authService.businessCurrent?.id) {
      return this.getResponseDefault()
    }
    params = this.getParamsCommon(params);
    const body = {
      params: params,
      responseType: resType
    }


    return this.httpClient.get<T>(this.getBaseUrlApi(endpoint), body).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  postRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    if (!this.authService.businessCurrent?.id) {
      return this.getResponseDefault()
    }

    params = this.getParamsCommon(params);
    params = parseParams(params);
    const options = {
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      map(res => this.normalizeV1Response(res)),
      this.handleError()
    )
  }

  /**
   * Chú ý API hầu hết đều dùng GET nên cần xem tài liệu trước khi dùng
   * khác  ở chỗ không có businessId
   * */
  getUserRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    params = this.getParamsCommon(params);
    const body = {
      params: params,
      responseType: resType
    }


    return this.httpClient.get<T>(this.getBaseUrlApi(endpoint), body).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  /**
   * khác postRequest ở chỗ không có businessId
   * @param endpoint
   * @param params
   * @param resType
   * @returns
   */
  postUserRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    params = parseParams(params);
    const options = {
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  postSystemRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    params = parseParams(params);
    const options = {
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  /**
   * Dùng cho các API POST dành cho GUEST có thể xem được
   * */
  postGuestRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    params = parseParams(params);
    const options = {
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  /**
    * Hàm sẽ nối thêm businessId để tiện theo dõi request của DN nào tiện lợi trong quá trình debug API khi cần
    * */
  getNewBaseUrlApi(endpoint: string, params: any): string {
    let url = environment.baseUrlApi + endpoint;

    // Lấy businessId từ params nếu có, nếu không có thì lấy từ authService
    const providedBusinessId = params && typeof params === 'object'
      ? params.businessId
      : undefined;
    const businessId = providedBusinessId ?? this.authService.businessCurrent?.id;

    // Kiểm tra điều kiện nối businessId vào URL
    if (
      businessId !== undefined &&
      businessId !== null &&
      !url.includes('businessId=')
    ) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}businessId=${encodeURIComponent(businessId)}`;
    }

    return url;
  }
}
